import {
  CREATIVE_SAMURAI_COURSE_FEE,
  formatCourseFee,
} from '../config/constants/creativeSamurai';

/** @typedef {'unpaid' | 'partial' | 'paid'} PaymentStatus */

/**
 * @param {{ discount_amount?: number | string | null }} enrollment
 * @returns {number}
 */
export const getDiscountAmount = (enrollment) => {
  const discount = Number(enrollment.discount_amount);
  return Number.isFinite(discount) && discount > 0 ? discount : 0;
};

/**
 * @param {{ discount_amount?: number | string | null }} enrollment
 * @returns {number}
 */
export const getEffectiveCourseFee = (enrollment) =>
  Math.max(0, CREATIVE_SAMURAI_COURSE_FEE - getDiscountAmount(enrollment));

/**
 * @param {{ amount_paid?: number | string | null }} enrollment
 * @returns {number}
 */
export const getAmountPaid = (enrollment) => Number(enrollment.amount_paid) || 0;

/**
 * @param {{ amount_paid?: number | string | null; discount_amount?: number | string | null }} enrollment
 * @returns {boolean}
 */
export const isFullyPaid = (enrollment) =>
  getAmountPaid(enrollment) >= getEffectiveCourseFee(enrollment);

/**
 * @param {{ amount_paid?: number | string | null; discount_amount?: number | string | null }} enrollment
 * @returns {PaymentStatus}
 */
export const getPaymentStatus = (enrollment) => {
  const amount = getAmountPaid(enrollment);
  const effectiveFee = getEffectiveCourseFee(enrollment);
  if (amount >= effectiveFee) return 'paid';
  if (amount > 0) return 'partial';
  return 'unpaid';
};

/**
 * @param {{ amount_paid?: number | string | null; discount_amount?: number | string | null }} enrollment
 * @returns {number}
 */
export const getRemainingBalance = (enrollment) => {
  const amount = getAmountPaid(enrollment);
  return Math.max(0, getEffectiveCourseFee(enrollment) - amount);
};

/**
 * @param {number} discountAmount
 * @returns {number}
 */
export const getDiscountPercent = (discountAmount) => {
  if (!discountAmount || CREATIVE_SAMURAI_COURSE_FEE <= 0) return 0;
  return Math.round((discountAmount / CREATIVE_SAMURAI_COURSE_FEE) * 100);
};

/**
 * @param {Array<{ amount_paid?: number | string | null }>} enrollments
 * @returns {number}
 */
export const getTotalIncome = (enrollments) =>
  enrollments.reduce((sum, enrollment) => sum + getAmountPaid(enrollment), 0);

/**
 * @param {Array<{ discount_amount?: number | string | null }>} enrollments
 * @returns {number}
 */
export const getTotalDiscountGiven = (enrollments) =>
  enrollments.reduce((sum, enrollment) => sum + getDiscountAmount(enrollment), 0);

export { CREATIVE_SAMURAI_COURSE_FEE, formatCourseFee };
